// src/types/auction.ts
var AUCTION_CATEGORIES = [
  { value: "electronics", label: "Electronics" },
  { value: "fashion", label: "Fashion" },
  { value: "collectibles", label: "Collectibles" },
  { value: "vehicles", label: "Vehicles" },
  { value: "home", label: "Home & Garden" },
  { value: "sports", label: "Sports" },
  { value: "toys", label: "Toys & Games" },
  { value: "art", label: "Art" },
  { value: "jewelry", label: "Jewelry" },
  { value: "other", label: "Other" }
];

// src/types/api.ts
var ErrorCodes = {
  // Auth errors
  INVALID_CREDENTIALS: "INVALID_CREDENTIALS",
  TOKEN_EXPIRED: "TOKEN_EXPIRED",
  UNAUTHORIZED: "UNAUTHORIZED",
  // Auction errors
  AUCTION_NOT_FOUND: "AUCTION_NOT_FOUND",
  AUCTION_NOT_ACTIVE: "AUCTION_NOT_ACTIVE",
  BID_TOO_LOW: "BID_TOO_LOW",
  CANNOT_BID_OWN_AUCTION: "CANNOT_BID_OWN_AUCTION",
  AUCTION_ENDED: "AUCTION_ENDED",
  // Wallet errors
  INSUFFICIENT_BALANCE: "INSUFFICIENT_BALANCE",
  HOLD_NOT_FOUND: "HOLD_NOT_FOUND",
  DEPOSIT_FAILED: "DEPOSIT_FAILED",
  // General errors
  VALIDATION_ERROR: "VALIDATION_ERROR",
  INTERNAL_ERROR: "INTERNAL_ERROR",
  NOT_FOUND: "NOT_FOUND"
};

// src/utils/validation.ts
var EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email) {
  return EMAIL_REGEX.test(email);
}
function isValidPassword(password) {
  return password.length >= 8;
}
function validateRegisterInput(input) {
  const errors = [];
  if (!input.email || !isValidEmail(input.email)) {
    errors.push("Valid email is required");
  }
  if (!input.password || !isValidPassword(input.password)) {
    errors.push("Password must be at least 8 characters");
  }
  if (!input.displayName || input.displayName.trim().length < 2) {
    errors.push("Display name must be at least 2 characters");
  }
  return { valid: errors.length === 0, errors };
}
function validateLoginInput(input) {
  const errors = [];
  if (!input.email || !isValidEmail(input.email)) {
    errors.push("Valid email is required");
  }
  if (!input.password) {
    errors.push("Password is required");
  }
  return { valid: errors.length === 0, errors };
}
function validateCreateAuctionInput(input) {
  const errors = [];
  if (!input.title || input.title.trim().length < 3) {
    errors.push("Title must be at least 3 characters");
  }
  if (!input.description || input.description.trim().length < 10) {
    errors.push("Description must be at least 10 characters");
  }
  if (!input.startingPrice || input.startingPrice < 100) {
    errors.push("Starting price must be at least \xA31.00");
  }
  if (input.reservePrice !== void 0 && input.reservePrice < input.startingPrice) {
    errors.push("Reserve price must be higher than starting price");
  }
  const now = /* @__PURE__ */ new Date();
  if (!input.startTime || new Date(input.startTime) < now) {
    errors.push("Start time must be in the future");
  }
  if (!input.endTime || new Date(input.endTime) <= new Date(input.startTime)) {
    errors.push("End time must be after start time");
  }
  const durationMs = new Date(input.endTime).getTime() - new Date(input.startTime).getTime();
  if (durationMs < 60 * 60 * 1e3) {
    errors.push("Auction must last at least 1 hour");
  }
  return { valid: errors.length === 0, errors };
}
function validatePlaceBidInput(input, currentBidAmount) {
  const errors = [];
  if (!input.auctionId) {
    errors.push("Auction ID is required");
  }
  if (!input.amount || input.amount <= 0) {
    errors.push("Bid amount must be positive");
  }
  if (input.amount <= currentBidAmount) {
    errors.push(`Bid must be higher than current bid of ${formatMoney(currentBidAmount)}`);
  }
  const minIncrement = 100;
  if (input.amount - currentBidAmount < minIncrement) {
    errors.push("Minimum bid increment is \xA31.00");
  }
  return { valid: errors.length === 0, errors };
}
function validateDepositInput(input) {
  const errors = [];
  if (!input.amount || input.amount < 100) {
    errors.push("Minimum deposit is \xA31.00");
  }
  if (input.amount > 1e6) {
    errors.push("Maximum deposit is \xA310,000.00");
  }
  if (!input.paymentNonce) {
    errors.push("Payment nonce is required");
  }
  return { valid: errors.length === 0, errors };
}
function formatMoney(cents) {
  return `\xA3${(cents / 100).toFixed(2)}`;
}

// src/utils/money.ts
function toPence(pounds) {
  return Math.round(pounds * 100);
}
function toPounds(pence) {
  return pence / 100;
}
function formatCurrency(pence) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP"
  }).format(toPounds(pence));
}
function formatCurrencyCompact(pence) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact"
  }).format(toPounds(pence));
}
function parseCurrency(value) {
  const cleaned = value.replace(/[£$€,\s]/g, "");
  const parsed = parseFloat(cleaned);
  if (isNaN(parsed)) {
    throw new Error(`Invalid currency value: ${value}`);
  }
  return toPence(parsed);
}
function calculateMinimumBid(currentBid, minimumIncrement = 100) {
  return currentBid + minimumIncrement;
}
function calculatePlatformFee(salePrice, feePercentage = 0) {
  return Math.round(salePrice * (feePercentage / 100));
}
function calculateSellerPayout(salePrice, feePercentage = 0) {
  return salePrice - calculatePlatformFee(salePrice, feePercentage);
}

// src/utils/id.ts
function generateId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.random() * 16 | 0;
    const v = c === "x" ? r : r & 3 | 8;
    return v.toString(16);
  });
}
function generateShortId(length = 8) {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    const values = new Uint8Array(length);
    crypto.getRandomValues(values);
    for (let i = 0; i < length; i++) {
      result += chars[values[i] % chars.length];
    }
  } else {
    for (let i = 0; i < length; i++) {
      result += chars[Math.floor(Math.random() * chars.length)];
    }
  }
  return result;
}
function isValidUuid(id) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
}
function createDeterministicId(input) {
  let hash = 0;
  for (let i = 0; i < input.length; i++) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return `${input}-${Math.abs(hash).toString(16)}`;
}
export {
  AUCTION_CATEGORIES,
  ErrorCodes,
  calculateMinimumBid,
  calculatePlatformFee,
  calculateSellerPayout,
  createDeterministicId,
  formatCurrency,
  formatCurrencyCompact,
  generateId,
  generateShortId,
  isValidEmail,
  isValidPassword,
  isValidUuid,
  parseCurrency,
  toPence,
  toPounds,
  validateCreateAuctionInput,
  validateDepositInput,
  validateLoginInput,
  validatePlaceBidInput,
  validateRegisterInput
};
